import { useState, useEffect } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { ChevronDown } from "lucide-react";
import { useSite } from "../context/SiteContext";

const FALLBACK_NAV = [
  { label: "Home", path: "/", children: [] },
  { label: "About", path: "/about", children: [] },
  { label: "Services", path: "/services", children: [] },
  { label: "Industries", path: "/industries", children: [] },
  { label: "Compliance", path: "/compliance", children: [] },
  { label: "Resources", path: "/resources", children: [] },
  { label: "Contact", path: "/contact", children: [] },
];

export const Header = () => {
  const { settings } = useSite();
  const [open, setOpen] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const location = useLocation();
  const logo = settings?.logo_path || "/lemur-logo.png";
  const nav = (settings?.navigation?.length ? settings.navigation : FALLBACK_NAV).filter((n) => n.visible !== false);
  const ctaLabel = settings?.cta_label || "Request a Consultation";

  useEffect(() => { setOpen(false); setExpanded(null); }, [location.pathname]);

  const testid = (label) => label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "");

  return (
    <>
      <header className="site-header" data-testid="site-header">
        <div className="wrap nav">
          <Link to="/" className="brand" data-testid="brand-home-link" aria-label="Lemur Quality home">
            <img src={logo} alt="Lemur Quality logo" />
            <span>
              <b>{settings?.name || "Lemur Quality"}</b>
              <small>{settings?.tagline || "Compliance Consultancy"}</small>
            </span>
          </Link>

          <nav className="menu" aria-label="Primary">
            {nav.map((n) => (
              n.children && n.children.length ? (
                <div className="has-drop" key={n.path} data-testid={`nav-${testid(n.label)}`}>
                  <NavLink to={n.path} className={({ isActive }) => `drop-trigger ${isActive ? "active" : ""}`}>
                    {n.label}
                    <ChevronDown size={14} strokeWidth={2.4} />
                  </NavLink>
                  <div className="dropdown" role="menu">
                    {n.children.map((c) => (
                      <NavLink key={c.path} to={c.path} className="drop-item" role="menuitem" data-testid={`dropdown-${testid(c.label)}`}>
                        <span className="di-label">{c.label}</span>
                        {c.description && <span className="di-desc">{c.description}</span>}
                      </NavLink>
                    ))}
                  </div>
                </div>
              ) : (
                <NavLink key={n.path} to={n.path} className={({ isActive }) => (isActive ? "active" : "")} data-testid={`nav-${testid(n.label)}`}>
                  {n.label}
                </NavLink>
              )
            ))}
          </nav>

          <Link to="/contact?intent=consultation" className="btn btn-primary desktop-cta" data-testid="header-cta">
            {ctaLabel}
          </Link>

          <button
            className="hamb"
            aria-label="Toggle menu"
            aria-expanded={open}
            onClick={() => setOpen((o) => !o)}
            data-testid="mobile-menu-toggle"
          >
            <span /><span /><span />
          </button>
        </div>
      </header>

      <div className={`mobile-menu ${open ? "open" : ""}`} data-testid="mobile-menu">
        {nav.map((n) => (
          n.children && n.children.length ? (
            <div className="m-group" key={n.path}>
              <button
                className="m-group-head"
                aria-expanded={expanded === n.path}
                onClick={() => setExpanded((e) => (e === n.path ? null : n.path))}
                data-testid={`mobile-group-${testid(n.label)}`}
              >
                {n.label}
                <ChevronDown size={16} className={expanded === n.path ? "rot" : ""} />
              </button>
              <div className={`m-sub ${expanded === n.path ? "open" : ""}`}>
                <NavLink to={n.path} className="m-sub-link overview" data-testid={`mobile-link-${testid(n.label)}-overview`}>
                  {n.label} overview
                </NavLink>
                {n.children.map((c) => (
                  <NavLink key={c.path} to={c.path} className="m-sub-link" data-testid={`mobile-link-${testid(c.label)}`}>
                    {c.label}
                  </NavLink>
                ))}
              </div>
            </div>
          ) : (
            <NavLink key={n.path} to={n.path} className={({ isActive }) => `m-top ${isActive ? "active" : ""}`} data-testid={`mobile-nav-${testid(n.label)}`}>
              {n.label}
            </NavLink>
          )
        ))}
        <Link to="/contact?intent=consultation" className="btn btn-primary" data-testid="mobile-header-cta">
          {ctaLabel}
        </Link>
      </div>
    </>
  );
};
